/* Il testo da incollare in Fatture Smart: un blocco solo.
 *
 * Dal 12 ottobre 2026. In studio la fattura si compone incollando un testo
 * intero nel campo Descrizione di Fatture Smart, non campo per campo: qui il
 * testo e' gia' composto dalle attivita' imputate, si puo' ritoccare (lo
 * studio lo ritocca sempre, per cliente) e si copia con un tasto.
 *
 * **Copiare salva anche i ritocchi.** Chi ritocca e copia fa un gesto solo,
 * e un tasto "Salva" separato si dimentica: il testo che e' stato incollato
 * davvero deve essere quello che il programma ricorda.
 *
 * **L'ultima volta si propone, non si applica.** Per l'assistenza periodica
 * l'elenco e' quasi sempre lo stesso di sei mesi prima: il programma lo offre
 * col periodo avanzato, e chi fattura sceglie. Gli anni dentro le voci
 * ("UNICO/2026") li scrive chi conosce il cliente e non si cambiano da soli:
 * si segnalano, perche' un anno sbagliato incollato in una fattura non lo
 * rilegge nessuno.
 */

import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ErroreApi, documenti } from "../api";
import { useCarica } from "../carica";
import { dataBreve, type TestoFatturaSmart } from "../dominio";
import { TastoCopia, copiaNegliAppunti } from "./SchedaCopia";

function righeDi(testo: string): number {
  return Math.min(26, Math.max(6, testo.split("\n").length + 1));
}

export function TestoFattura({ identificativo }: { identificativo: string }) {
  const caricato = useCarica<TestoFatturaSmart>(() => documenti.testo(identificativo), [identificativo]);
  const t = caricato.dati;

  const [bozza, setBozza] = useState("");
  const [daUltimaVolta, setDaUltimaVolta] = useState(false);
  const [esito, setEsito] = useState<"" | "copiato" | "copiato-e-salvato" | "non-riuscito">("");
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  // Quando arriva (o cambia) il testo salvato, la casella riparte da quello.
  useEffect(() => {
    if (t !== undefined) {
      setBozza(t.testo);
      setDaUltimaVolta(false);
    }
  }, [t?.testo]);

  if (caricato.errore) {
    return (
      <section className="testo-fattura" aria-label="Testo per Fatture Smart">
        <h3>Testo per Fatture Smart</h3>
        <p className="errore" role="alert">
          {caricato.errore}
        </p>
      </section>
    );
  }
  if (t === undefined) {
    return (
      <section className="testo-fattura" aria-label="Testo per Fatture Smart">
        <h3>Testo per Fatture Smart</h3>
        <p className="attesa">Un momento.</p>
      </section>
    );
  }

  const modificato = bozza !== t.testo;
  const ultima = t.ultima_volta;

  async function salva(): Promise<boolean> {
    setErrore(null);
    setInCorso(true);
    try {
      await documenti.salvaTesto(identificativo, bozza);
      await caricato.ricarica();
      return true;
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : "Non sono riuscito a salvare il testo.");
      return false;
    } finally {
      setInCorso(false);
    }
  }

  async function copia() {
    setErrore(null);
    const riuscito = await copiaNegliAppunti(bozza);
    if (!riuscito) {
      setEsito("non-riuscito");
    } else if (modificato && t?.modificabile) {
      const salvato = await salva();
      setEsito(salvato ? "copiato-e-salvato" : "copiato");
    } else {
      setEsito("copiato");
    }
    setTimeout(() => setEsito(""), 2400);
  }

  function tornaAlSalvato() {
    setBozza(t!.testo);
    setDaUltimaVolta(false);
    setErrore(null);
  }

  function usaUltimaVolta() {
    if (!ultima) return;
    setBozza(ultima.testo);
    setDaUltimaVolta(true);
    setErrore(null);
  }

  const idArea = `testo-${identificativo}`;

  return (
    <section className="testo-fattura" aria-label="Testo per Fatture Smart">
      <div className="copia-testa">
        <h3>
          <label htmlFor={idArea}>Testo per Fatture Smart</label>
        </h3>
        {!t.modificabile && <span className="narrativa">già partito: non si cambia più</span>}
      </div>

      {t.da_guardare.map((testo) => (
        <p key={testo} className="riga-avviso">
          {testo.charAt(0).toUpperCase() + testo.slice(1)}
        </p>
      ))}

      <textarea
        id={idArea}
        className="testo-fattura-area"
        value={bozza}
        readOnly={!t.modificabile}
        rows={righeDi(bozza)}
        spellCheck={false}
        onChange={(e) => {
          setBozza(e.target.value);
          setDaUltimaVolta(false);
        }}
      />

      <div className="testo-fattura-azioni">
        <button type="button" className="tasto tasto-principale" onClick={copia} disabled={inCorso}>
          {esito === "copiato-e-salvato"
            ? "Copiato e salvato"
            : esito === "copiato"
              ? "Copiato"
              : esito === "non-riuscito"
                ? "Copia non riuscita"
                : "Copia il testo"}
        </button>
        {t.modificabile && modificato && (
          <>
            <button type="button" className="tasto" onClick={() => void salva()} disabled={inCorso}>
              Salva i ritocchi
            </button>
            <button type="button" className="tasto tasto-nudo" onClick={tornaAlSalvato} disabled={inCorso}>
              Torna al testo salvato
            </button>
          </>
        )}
        {t.modificabile && ultima && !daUltimaVolta && (
          <button type="button" className="tasto tasto-nudo" onClick={usaUltimaVolta} disabled={inCorso}>
            Usa il testo dell'ultima volta (
            {ultima.tipo === "FATTURA"
              ? `fattura${ultima.numero ? ` n. ${ultima.numero}` : ""}`
              : "avviso"}{" "}
            del {dataBreve(ultima.data)})
          </button>
        )}
      </div>

      {daUltimaVolta && ultima && (
        <div className="segnalazioni">
          {ultima.periodo_avanzato && (
            <p className="riga-avviso">
              Il periodo nella prima riga è stato portato avanti: controllalo.
            </p>
          )}
          {ultima.anni.length > 0 && (
            <p className="riga-avviso">
              Dentro le voci compaiono gli anni {ultima.anni.join(", ")}: sono quelli dell'ultima
              volta, controllali prima di copiare.
            </p>
          )}
          <p className="aiuto">Non è ancora salvato: lo diventa quando copi o salvi.</p>
        </div>
      )}

      {errore && (
        <p className="errore" role="alert">
          {errore}
        </p>
      )}

      <table className="tabella copia-tabella testo-fattura-campi">
        <tbody>
          <tr>
            <td className="copia-etichetta">Importo della riga</td>
            <td>
              <span className="copia-valore">{t.importo}</span>
            </td>
            <td className="copia-tasto">
              <TastoCopia testo={t.importo} />
            </td>
          </tr>
        </tbody>
      </table>
      <p className="aiuto">
        Il testo va nel campo Descrizione della riga di Fatture Smart, l'importo nel suo campo.
        Cassa e IVA li calcola Fatture Smart: le cifre da riguardare sono nel calcolo qui sopra. Le frasi delle
        voci vengono dal <Link to="/impostazioni/catalogo">catalogo</Link>: se una non va bene, si cambia lì.
      </p>
    </section>
  );
}
